import { useMemo } from 'react';
import { useQuery } from 'react-query';

import {
  DepartmentPrivilegeBase,
  filterDepartmentsByPrivilege,
} from 'src/utils/filterDepartmentsByPrivilege';

import { useAuthContext } from 'src/auth/hooks';
import { queryKeys } from 'src/constants/queryKeys';
import { getAllDepartment } from 'src/services/category';

type UseDepartmentsByPrivilegeOptions = {
  enabled?: boolean;
  staleTime?: number;
  cacheTime?: number;
  retry?: number | boolean;
  refetchOnWindowFocus?: boolean | 'always';
  refetchOnMount?: boolean | 'always';
  refetchOnReconnect?: boolean | 'always';
};

/**
 * 按模块部门读取权限获取可选部门列表。
 * 页面只需传入 privilegeBase，无需自行读取 permissions。
 */
export function useDepartmentsByPrivilege(
  privilegeBase: DepartmentPrivilegeBase,
  options?: UseDepartmentsByPrivilegeOptions
) {
  const { user } = useAuthContext();

  const {
    data: allDepartments = [],
    isLoading,
    isError,
    ...rest
  } = useQuery([queryKeys.getAllDepartment], () => getAllDepartment(), {
    staleTime: 1000 * 60 * 60,
    enabled: options?.enabled,
    cacheTime: options?.cacheTime,
    retry: options?.retry,
    refetchOnWindowFocus: options?.refetchOnWindowFocus,
    refetchOnMount: options?.refetchOnMount,
    refetchOnReconnect: options?.refetchOnReconnect,
  });

  const departments = useMemo(
    () =>
      filterDepartmentsByPrivilege(
        allDepartments,
        user?.category?.id,
        user?.permissions,
        privilegeBase
      ),
    [allDepartments, user?.category?.id, user?.permissions, privilegeBase]
  );

  const departmentOptions = useMemo(
    () =>
      departments.map((dept) => ({
        label: dept.name,
        value: String(dept.id),
      })),
    [departments]
  );

  return {
    departments,
    departmentOptions,
    allDepartments,
    isLoading,
    isError,
    ...rest,
  };
}
